import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Heart, Loader2, Receipt, Share2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const POLL_MS = 2000;
const MAX_POLLS = 20;

export default function GiveThanks() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const attempts = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState({ phase: "failed" }); return; }
    try {
      const res = await api.get(`/payments/status/${encodeURIComponent(sessionId)}`);
      if (res.data.payment_status === "paid") { setState({ phase: "paid", ...res.data }); return; }
      if (["failed", "expired"].includes(res.data.payment_status)) {
        setState({ phase: "failed", ...res.data }); return;
      }
    } catch { setState({ phase: "failed" }); return; }
    attempts.current += 1;
    if (attempts.current >= MAX_POLLS) { setState({ phase: "timeout" }); return; }
    setTimeout(poll, POLL_MS);
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  const share = async () => {
    const url = `${window.location.origin}/give`;
    const text = `I've just given to help. If you can, join me: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ text, url }); return; } catch { /* fall through */ }
    }
    await navigator.clipboard.writeText(text);
    toast.success("Share text copied");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border bg-card/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2.5 px-4 py-3">
          <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
            <Heart className="h-3.5 w-3.5 text-white" />
          </span>
          <div className="text-[13px] font-semibold text-foreground">Your gift</div>
          <Button asChild variant="ghost" size="sm"
                  className="ml-auto rounded-full text-[11.5px] text-muted-foreground">
            <Link to="/give" data-testid="give-thanks-again">Give again</Link>
          </Button>
        </div>
      </header>

      <main className="grid flex-1 place-items-center px-4 py-10">
        <div className="panel w-full max-w-md p-8 text-center" data-testid="give-thanks">
        {state.phase === "checking" && (
          <>
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-primary" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              Confirming your gift
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              A few seconds — please do not close the window.
            </p>
          </>
        )}
        {state.phase === "paid" && (
          <div data-testid="give-thanks-paid">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              Thank you — {state.currency} {state.amount?.toLocaleString()} received
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              Credited to {state.fund_code}
              {state.donation_reference ? ` as ${state.donation_reference}` : ""}.
              {state.anonymous
                ? " You gave anonymously, so no receipt is issued and your name is nowhere on it."
                : state.receipt_no
                  ? " Your receipt is below and on your supporter account."
                  : " Our finance team will confirm it and send your receipt."}
            </p>
            {state.receipt_no && (
              <div className="mt-3 flex items-center justify-center gap-1.5 text-[11.5px] text-primary"
                   data-testid="give-thanks-receipt">
                <Receipt className="h-3.5 w-3.5" /> {state.receipt_no}
              </div>
            )}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Button onClick={share} data-testid="give-thanks-share"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Share2 className="mr-2 h-4 w-4" /> Ask a friend to join in
              </Button>
              <Button asChild variant="outline" className="rounded-full border-border">
                <Link to="/donor/signin" data-testid="give-thanks-account">
                  <Heart className="mr-2 h-4 w-4" /> See my giving
                </Link>
              </Button>
            </div>
          </div>
        )}
        {(state.phase === "failed" || state.phase === "timeout") && (
          <div data-testid="give-thanks-failed">
            <XCircle className="mx-auto h-8 w-8 text-red-500" />
            <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
              We could not confirm this payment
            </h1>
            <p className="mt-2 text-[12.5px] text-muted-foreground">
              If your card was charged, the gift will still reach the charity — their finance team
              reconciles every payment.
            </p>
            <Button asChild variant="outline" className="mt-6 rounded-full border-border">
              <Link to="/give"><Copy className="mr-2 h-4 w-4" /> Try again</Link>
            </Button>
          </div>
        )}
        </div>
      </main>

      <footer className="border-t border-border px-4 py-4 text-center text-[11px] text-muted-foreground">
        Every gift is tracked against a named fund and reported back to you. Keep your receipt for
        your tax return.
      </footer>
    </div>
  );
}
